import { Timestamp } from '@bufbuild/protobuf/wkt'
import DOMPurify from 'dompurify'
import { CopyIcon, SquarePenIcon } from 'lucide-react'
import { useCallback, useRef, useState } from 'react'
import { Quill } from 'react-quill-new'

import { useListAssessmentReports, useUpdateAssessmentReport } from '@/api/assessment-reports.hook'
import { useGetOngoingAssessment } from '@/api/assessments.hook'
import { useGetCompany } from '@/api/company.hook'
import { AssessmentDecision } from '@/gen/inventory/v1/assessment_decision_pb'

import { assessmentDecisionButtonLabel } from '@/const/label'

import { FinishAssessmentModal } from '@/pages/assessment/components/finish-assessment-modal/finish-assessment-modal'

import { Button } from '@/components/lemmala-design-system/button/button'
import { TextWithIcon } from '@/components/lemmala-design-system/text-with-icon/text-with-icon'
import { toast } from '@/components/lemmala-design-system/toast/use-toast'
import { AssessmentReportHeader } from '@/components/shared/assessment-report/assessment-report-header'
import { AssessmentReportLayout } from '@/components/shared/assessment-report/assessment-report-layout'
import { RegenerateReportButton } from '@/components/shared/assessment-report/regenerate-report-button'
import { AssessmentDecisionIcon } from '@/components/shared/icons/assessment-decision'

import { CONCLUSION_APPROVAL_VIEW_LABELS } from './labels'

export type ConclusionApprovalViewProps = {
  assessmentId: string
  companyId: string
  onFinishAssessment?: (hasRejectedAssessment: boolean, isFinalAssessment: boolean) => void
  readonly: boolean
}

// ConclusionApprovalView renders the AWAITING_APPROVAL state: the generated
// summary (editable by the approver, layout mirrors PastAssessmentReport) plus
// Reject / Approve buttons that drive the existing finalize modal. Per PRD the
// Reject/Approve buttons stay clickable even while summary generation is still
// running — they don't depend on report content.
export const ConclusionApprovalView = ({
  assessmentId,
  companyId,
  onFinishAssessment,
  readonly,
}: ConclusionApprovalViewProps) => {
  const {
    reports,
    reportContent,
    isPending: isReportsLoading,
  } = useListAssessmentReports(assessmentId)
  const { mutateAsync: updateReport, isPending: isSaving } = useUpdateAssessmentReport(assessmentId)
  const { data: company } = useGetCompany(companyId)
  const { data: ongoingAssessmentData } = useGetOngoingAssessment(assessmentId)
  const ongoing = ongoingAssessmentData?.ongoingAssessment
  const concludedBy = ongoing?.concludedBy
  const [isEditing, setIsEditing] = useState(false)
  const [finishModal, setFinishModal] = useState<{ open: boolean; decision: AssessmentDecision }>({
    open: false,
    decision: AssessmentDecision.APPROVED,
  })
  const quillRef = useRef<Quill | null>(null)

  const report = reports[0]
  const isReportReady = reportContent !== ''

  const handleQuillReady = useCallback((quill: Quill) => {
    quillRef.current = quill
  }, [])

  const handleSave = useCallback(async () => {
    if (!quillRef.current || !report?.id) return
    const htmlContent = quillRef.current.root.innerHTML
    try {
      await updateReport({
        reportId: report.id,
        content: new TextEncoder().encode(htmlContent),
      })
    } catch (error) {
      toast({
        variant: 'destructive',
        title: CONCLUSION_APPROVAL_VIEW_LABELS.SAVE_ERROR_TITLE,
        description:
          error instanceof Error
            ? error.message
            : CONCLUSION_APPROVAL_VIEW_LABELS.SAVE_ERROR_DESCRIPTION,
      })
    } finally {
      setIsEditing(false)
    }
  }, [report, updateReport])

  const handleCopyToClipboard = useCallback(async () => {
    const tempDiv = document.createElement('div')
    tempDiv.innerHTML = DOMPurify.sanitize(reportContent)
    const plainText = tempDiv.textContent || tempDiv.innerText || ''
    await navigator.clipboard.writeText(plainText)
  }, [reportContent])

  return (
    <div className='flex h-full min-h-0 flex-col'>
      <AssessmentReportLayout
        isReportLoading={isReportsLoading || !isReportReady}
        isEditing={isEditing}
        reportContent={reportContent}
        onQuillReady={handleQuillReady}
        header={
          ongoing?.createdAt && (
            <AssessmentReportHeader
              score={ongoing.score}
              companyName={company?.company?.profile?.name ?? ''}
              projectName={ongoing.projectName}
              createdAt={ongoing.createdAt as Timestamp}
              actions={
                !readonly && isReportReady ? (
                  <div className='flex gap-2 pr-10'>
                    {isEditing ? (
                      <>
                        <Button
                          variant='outline'
                          onClick={() => setIsEditing(false)}
                          disabled={isSaving}
                        >
                          {CONCLUSION_APPROVAL_VIEW_LABELS.CANCEL}
                        </Button>
                        <RegenerateReportButton
                          ongoingAssessmentId={assessmentId}
                          quillRef={quillRef}
                          onRegenerated={() => setIsEditing(false)}
                        />
                        <Button onClick={handleSave} disabled={isSaving}>
                          {isSaving
                            ? CONCLUSION_APPROVAL_VIEW_LABELS.SAVING
                            : CONCLUSION_APPROVAL_VIEW_LABELS.SAVE}
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          variant='ghost'
                          size='icon'
                          onClick={handleCopyToClipboard}
                          data-testid='copy-risk-report-button'
                        >
                          <CopyIcon className='size-4' />
                        </Button>
                        <Button variant='ghost' size='icon' onClick={() => setIsEditing(true)}>
                          <SquarePenIcon className='size-4' />
                        </Button>
                      </>
                    )}
                  </div>
                ) : undefined
              }
              concludedBy={concludedBy}
              concludedAt={ongoing.concludedAt}
            />
          )
        }
      />
      {!readonly && (
        <div className='mb-6 flex items-center justify-end gap-2'>
          <Button
            disabled={isEditing}
            variant='destructive'
            onClick={() => setFinishModal({ open: true, decision: AssessmentDecision.REJECTED })}
          >
            <TextWithIcon
              icon={<AssessmentDecisionIcon decision={AssessmentDecision.REJECTED} />}
              text={assessmentDecisionButtonLabel[AssessmentDecision.REJECTED]}
            />
          </Button>
          <Button
            disabled={isEditing}
            onClick={() => setFinishModal({ open: true, decision: AssessmentDecision.APPROVED })}
          >
            <TextWithIcon
              icon={<AssessmentDecisionIcon decision={AssessmentDecision.APPROVED} />}
              text={assessmentDecisionButtonLabel[AssessmentDecision.APPROVED]}
            />
          </Button>
        </div>
      )}
      {finishModal.open && (
        <FinishAssessmentModal
          open={finishModal.open}
          onClose={() => setFinishModal((s) => ({ ...s, open: false }))}
          decision={finishModal.decision}
          assessmentId={assessmentId}
          companyId={companyId}
          onFinishAssessment={onFinishAssessment}
        />
      )}
    </div>
  )
}
